import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router';
import { DeleteAccountConfirm } from '../components/DeleteAccountConfirm';
import PolicyConfirmations from '../components/PolicyConfirmations';
import { useApp } from '../context/AppContext';
import type { PolicyConfirmationsValue } from '../services/authService';
import { notify } from '../utils/notifications';
import { manageAISharing } from '../services/aiSharingService';

export default function PolicyConsentScreen() {
  const navigate = useNavigate();
  const {
    acceptCurrentPolicy,
    authSession,
    authUser,
    currentEventId,
    deleteAccount,
    signOut,
  } = useApp();
  const [confirmations, setConfirmations] = useState<PolicyConfirmationsValue>({
    adultAttested: false,
    termsAccepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canContinue = confirmations.adultAttested && confirmations.termsAccepted;

  async function handleContinue() {
    const owner = authSession;
    if (!owner || !canContinue || submitting) return;
    setSubmitting(true);
    try {
      await acceptCurrentPolicy(owner);
      if (!authUser?.is_verified) {
        navigate('/verify-email', { replace: true });
      } else if (!authUser.profile_complete) {
        navigate('/complete-profile', { replace: true });
      } else {
        navigate(currentEventId ? '/home' : '/my-conferences', { replace: true });
      }
    } catch (error) {
      notify.error(
        'Could not save your confirmation',
        error instanceof Error ? error.message : 'Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/signin', { replace: true });
  }

  async function handleDelete(confirmEmail: string) {
    const owner = authSession;
    if (!owner) throw new Error('Not authenticated.');
    await deleteAccount(confirmEmail, owner);
    navigate('/signin', { replace: true });
  }

  return (
    <main className="size-full overflow-y-auto px-6 pb-10" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div className="pt-14 pb-8">
        <div
          className="size-16 rounded-2xl border flex items-center justify-center mb-6"
          style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}
        >
          <ShieldCheck className="size-8" style={{ color: 'var(--app-accent)' }} aria-hidden="true" />
        </div>
        <h1 className="text-[30px] tracking-tight" style={{ color: 'var(--app-text)' }}>
          One step before you continue
        </h1>
        <p className="mt-3 text-sm leading-6" style={{ color: 'var(--app-text-muted)' }}>
          Rubikoo is for adults. Confirm your age and accept the current Terms of Service to access event content.
        </p>
      </div>

      <PolicyConfirmations
        value={confirmations}
        onChange={setConfirmations}
        disabled={submitting}
      />

      <button
        type="button"
        onClick={() => { void handleContinue(); }}
        disabled={!canContinue || submitting || !authSession}
        className="mt-5 h-14 w-full rounded-xl text-white text-lg disabled:opacity-50"
        style={{ backgroundColor: 'var(--app-accent-solid)' }}
      >
        {submitting ? 'Saving…' : 'Continue'}
      </button>

      <div className="mt-8 rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--app-border)' }}>
        <button type="button" onClick={() => void manageAISharing(authSession?.signal).catch(() => notify.error('Could not load AI sharing settings'))} className="w-full px-4 py-3.5 text-left text-sm" style={{ color: 'var(--app-text)' }}>Rubikoo AI settings</button>
        <button
          type="button"
          onClick={() => { void handleSignOut(); }}
          className="w-full px-4 py-3.5 text-left text-sm"
          style={{ color: 'var(--app-text)' }}
        >
          Sign out
        </button>
        <div className="border-t" style={{ borderColor: 'var(--app-border)' }}>
          <DeleteAccountConfirm
            open={deleteOpen}
            email={authUser?.email ?? ''}
            onConfirm={handleDelete}
            onOpenChange={setDeleteOpen}
          />
        </div>
      </div>
    </main>
  );
}
